import React, { useEffect, useState, createContext, useContext, useCallback, useMemo, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { logout } from "./views/auth/util";
import axiosInstance from "./api/axiosInstance";
import RequireCapability from "./components/auth/RequireCapability";
import { ToastProvider } from "./components/toast/Toast";
import { normalizeRole } from "./utils/roleNormalizer";
import './index.css';
import ScrollToTop from "./utils/ScrollToTop";
import ProjectAdminLayout from "./layouts/projectadmin";
import AuthLayout from "./layouts/auth";
import SuperAdminLayout from "./layouts/superadmin";
import ManagerLayout from "./layouts/manager";
import SupportAgentLayout from "./layouts/support";
import EmployeeLayout from "./layouts/employee";
import ApplicationLayout from "./layouts/application";
import PrimeReact from "primereact/api";

// ===== Project Admin - Support Agents =====
const AddSupport = lazy(() => import("./views/projectadmin/supportagent/AddSupport"));
const EditSupport = lazy(() => import("./views/projectadmin/supportagent/EditSupport"));
const SupportAgentDirectory = lazy(() => import("./views/projectadmin/supportagent/SupportAgentDirectory"));
const SupportAgentOverview = lazy(() => import("./views/projectadmin/supportagent/SupportAgentOverview"));
const BulkUploadSupport = lazy(() => import("./views/projectadmin/supportagent/BulkUploadSupport"));

// ===== Project Admin - Employees =====
const EmployeeDirectory = lazy(() => import("./views/projectadmin/employee/EmployeeDirectory"));
const AddEmployee = lazy(() => import("./views/projectadmin/employee/AddEmployee"));
const EmployeeOverview = lazy(() => import("./views/projectadmin/employee/EmployeeOverview"));
const BulkUploadEmployee = lazy(() => import("./views/projectadmin/employee/BulkUploadEmployee"));

// ===== Project Admin - Queues =====
const QueueDirectory = lazy(() => import("./views/projectadmin/queues/QueueDirectory"));
const QueueOverview = lazy(() => import("./views/projectadmin/queues/QueueOverview"));
const AddQueue = lazy(() => import("./views/projectadmin/queues/AddQueue"));
const EditQueue = lazy(() => import("./views/projectadmin/queues/EditQueue"));

// ===== Project Admin - SLA & Knowledge Base =====
const SlaPolicies = lazy(() => import("./views/projectadmin/SLA/SlaPolicies"));
const KbDirectory = lazy(() => import("./views/projectadmin/knowledgebase/KbDirectory"));

// Auth pages
const SignIn = lazy(() => import("./views/auth/SignIn"));
const ClientSignIn = lazy(() => import("./views/auth/ClientSignIn"));
const ForgotPassword = lazy(() => import("./views/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./views/auth/ResetPassword"));
const SignUp = lazy(() => import("./views/auth/SignUp"));
const ApplicationRequestForm = lazy(() => import("./views/iframe/ApplicationRequestForm"));
const PublicDashboardForm = lazy(() => import("./views/iframe/PublicDashboardForm"));

const ProfileOverview = lazy(() => import("./views/projectadmin/profile/profile"));

// ===== Project Admin - App & Domain =====
const AppDomain = lazy(() => import("./views/projectadmin/settings"));
// const SLAPolicies = lazy(() => import("./views/projectadmin/features/management/SLAPolicies"));
const ApplicationOverview = lazy(() => import("./views/projectadmin/settings/ApplicationOverview"));
const CustomForm = lazy(() => import("./views/projectadmin/settings/shared/CustomForm"));
const FormRenderer = lazy(() => import("./views/projectadmin/settings/shared/FormRender"));
const DepartmentOverview = lazy(() => import("./views/projectadmin/settings/DepartmentOverview"));

// ===== Project Admin - Knowledge Base =====
const KbCategory = lazy(() => import("./views/projectadmin/knowledgebase/KbCategory"));
const AddKb = lazy(() => import("./views/projectadmin/knowledgebase/AddKb"));
const EditKb = lazy(() => import("./views/projectadmin/knowledgebase/EditKb"));
const BulkUploadKb = lazy(() => import("./views/projectadmin/knowledgebase/BulkUploadKb"));
const NotificationsPage = lazy(() => import("./components/notifications/NotificationsPage"));
const KbOverview = lazy(() => import("./views/projectadmin/knowledgebase/KbOverview"));

// ===== Project Admin - Support =====
const TicketsDirectory = lazy(() => import("./views/projectadmin/tickets/TicketsDirectory"));
const TicketsOverview = lazy(() => import("./views/projectadmin/tickets/TicketsOverview"));

// ===== Project Admin - Analytics =====
const Reports = lazy(() => import("./views/projectadmin/reports/Reports"));
const ProjectAdminDashboard = lazy(() => import("./views/projectadmin/dashboard/ProjectAdminDashboard"));

// ===== Project Admin - Testing =====
const Testing = lazy(() => import("./views/projectadmin/testing/Testing"));

// === SuperAdmin pages ===
const DashboardOverview = lazy(() => import("./views/superadmin/dashboard/DashboardOverview"));
const AddClient = lazy(() => import("./views/superadmin/clients/clientActions/addAction/AddClient"));
const AddDomainPage = lazy(() => import("./views/superadmin/clients/clientActions/addAction/Add Domain"));
const EditDomain = lazy(() => import("./views/superadmin/clients/clientActions/editAction/Edit domain"));
const AddApplication = lazy(() => import("./views/superadmin/clients/clientActions/addAction/Add Application"));
const EditApplication = lazy(() => import("./views/superadmin/clients/clientActions/editAction/Edit Application"));
const AddProjectAdmin = lazy(() => import("./views/superadmin/clients/clientActions/addAction/Add ProjectAdmin"));
const EditProjectAdmin = lazy(() => import("./views/superadmin/clients/clientActions/editAction/Edit projectadmin"));
const AddProjectAdminWithDropdown = lazy(() => import("./views/superadmin/clients/clientActions/addAction/AddProjectAdminWithDropdown"));
const ClientsManagement = lazy(() => import("./views/superadmin/clients/ClientManagement/ClientsManagement"));
const EditClient = lazy(() => import("./views/superadmin/clients/clientActions/editAction/EditClient"));
const ProjectAdminsPage = lazy(() => import("./views/superadmin/projectadmins/viewprojectadmin"));
const ProjectAdminDetailsPage = lazy(() => import("./views/superadmin/projectadmins/ProjectAdmin"));
const SupportAgentsPage = lazy(() => import("./views/superadmin/supportagents/viewsupportagents"));
const AgentDetailsPage = lazy(() => import("./views/superadmin/supportagents/AgentDetails"));
const ClientDashboardOverview = lazy(() => import("./views/superadmin/clients/clientDashboardOverview/clientdashboard"));
const Profile = lazy(() => import("./views/superadmin/profile/Profile"));
const Settings = lazy(() => import("./views/superadmin/settings/Settings"));
const Report = lazy(() => import("./views/superadmin/reports/Report"));
const SLAManagement = lazy(() => import("./views/superadmin/sla/SLAManagement"));
const SupportChannels = lazy(() => import("./views/superadmin/support-channels"));

// === Manager (SLA approvals) ===
const ManagerSLAApprovals = lazy(() => import("./views/manager/sla-approvals"));

// -------------SUPPORT AGENT PAGES--------------
const Overview = lazy(() => import("./views/supportagent/Overview/Overview"));
const TicketPage = lazy(() => import("./views/supportagent/tickets/TicketPage"));
const TicketDetail = lazy(() => import("./views/supportagent/tickets/TicketDetail"));
const KnowledgeBase = lazy(() => import("./views/supportagent/KnowledgeBase"));
const NewKBDraft = lazy(() => import("./views/supportagent/KnowledgeBase/NewKBDraft"));
const PreviewKBSupportAgents = lazy(() => import("./views/supportagent/KnowledgeBase/previewKB"));
const Queues = lazy(() => import("./views/supportagent/queues"));
const SupportProfile = lazy(() => import("./views/supportagent/profile/SupportProfile"));
const AgentFormDesigner = lazy(() => import("./views/supportagent/forms/AgentFormDesigner"));
const AgentFormsList = lazy(() => import("./views/supportagent/forms/AgentFormsList"));
const AgentFormView = lazy(() => import("./views/supportagent/forms/AgentFormView"));

//---------------EMPLOYEE PAGES--------------------
const EmployeeDashboard = lazy(() => import("./views/employee/home/dashboard"));
const MyRequest = lazy(() => import("./views/employee/myrequest/MyRequest"));
const RequestOverview = lazy(() => import("./views/employee/myrequest/RequestOverview"));
const RequestFormPage = lazy(() => import("./views/employee/home/requestform/RequestFormPage"));
const ServiceCatalog = lazy(() => import("./views/employee/servicecatalog/ServiceCatalog"));
const CatalogFormPage = lazy(() => import("./views/employee/servicecatalog/CatalogFormPage"));
const EmployeeProfile = lazy(() => import("./views/employee/profile/EmployeeProfile"));
const Chatbot = lazy(() => import("./views/employee/home/Chatbot"));

//---------------APPLICATION PAGES--------------------
const ApplicationDashboard = lazy(() => import("./views/application/home/dashboard"));
const ApplicationMyRequest = lazy(() => import("./views/application/myrequest/MyRequest"));
const ApplicationRequestOverview = lazy(() => import("./views/application/myrequest/RequestOverview"));
const ApplicationRequestFormPage = lazy(() => import("./views/application/home/requestform/RequestFormPage"));
const ApplicationProfile = lazy(() => import("./views/application/profile/EmployeeProfile"));
const ApplicationChatbot = lazy(() => import("./views/application/home/Chatbot"));

// ---------------ERROR PAGES--------------------
const UnauthorizedPage = lazy(() => import("./views/error/UnauthorizedPage"));
const NotFoundPage = lazy(() => import("./views/error/NotFoundPage"));
const LoginRequiredPage = lazy(() => import("./views/error/LoginRequiredPage"));

PrimeReact.hideOverlaysOnDocumentScrolling = false;

export const InitialDataContext = createContext(null);
export const AuthContext = createContext(null);

/* ------------------ AUTH PROVIDER ------------------ */
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkTokenExpiry = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/v1/auth/status", {
        withCredentials: true,
      });
      const rawRole =
        response.data.user_type || response.data.role || response.data.user?.role;

      const data = response.data;
      if (data.authenticated && data.user) {
        const userData = {
          ...response.data.user,
          role: normalizeRole(rawRole),
        };
        setUser((prevUser) => {
          if (JSON.stringify(prevUser) === JSON.stringify(userData)) return prevUser;
          return userData;
        });
        localStorage.setItem("user", JSON.stringify(userData));
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await axiosInstance.get("/api/v1/auth/status", {
          withCredentials: true,
        });

        if (response.data.authenticated && response.data.user) {
          const rawRole = response.data.user_type || 
                          response.data.role || 
                          response.data.user?.role;
          
          const normalizedRole = normalizeRole(rawRole);
          const userData = {
            ...response.data.user,
            role: normalizedRole,
            original_role: rawRole
          };
          
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));
        } else {
          setUser(null);
        }
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    const interval = setInterval(() => checkTokenExpiry(), 60000);
    return () => clearInterval(interval);
  }, [checkTokenExpiry]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      // DEF-178: removing three known keys leaves everything else another user
      // wrote in this tab — cached ticket titles, and the AI chat's session_id
      // and in-progress ticket state (which live in sessionStorage). The next
      // employee to sign in then resumed the previous one's chat. Wipe both
      // stores so a new session starts genuinely empty.
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch { /* storage unavailable — non-fatal */ }
    } catch {
      setUser(null);
    }
  }, []);

  const authValue = useMemo(
    () => ({
      user,
      setUser,
      logout: handleLogout,
      refreshToken: checkTokenExpiry,
      isAuthenticated: !!user,
      loading,
    }),
    [user, loading, handleLogout, checkTokenExpiry]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

/* ------------------ ROLE-BASED ROUTE GUARD ------------------ */
const RoleBasedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login-required"
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  const userRole = user?.role ? normalizeRole(user.role) : null;
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

/* ------------------ META HOOK ------------------ */
const useDocumentMeta = () => {
  const location = useLocation();
  useEffect(() => {
    const isClient = location.pathname.startsWith("/client/");
    document.title = isClient ? "Client Portal" : "ServiceLoom";
  }, [location.pathname]);
};

const RouteLoading = () => (
  <div className="flex items-center justify-center h-screen bg-[#F4F7FE]">
    <div className="flex items-center gap-3 px-6 py-4 rounded-xl bg-white shadow-md border border-[#E2E8F0] text-[#206CEF] font-medium text-sm">
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#206CEF] border-t-transparent" />
      Loading...
    </div>
  </div>
);

/* ------------------ MAIN APP CONTENT ------------------ */
const AppContent = () => {
  useDocumentMeta();
  const { loading } = useContext(AuthContext);

  if (loading)
    return <RouteLoading />;

  return (
    <>
      <ScrollToTop />

      <Suspense fallback={<RouteLoading />}>
        <Routes>
        {/* Auth Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="sign-in" element={<SignIn />} />
          <Route path="client-login" element={<ClientSignIn />} />
          <Route path="sign-up" element={<SignUp />} />
          <Route path="sign-up/:userType" element={<SignUp />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
        </Route>

        {/* Error Pages */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/login-required" element={<LoginRequiredPage />} />
        <Route path="/404" element={<NotFoundPage />} />

        {/* Project Admin Routes - Protected with Role Check */}
        <Route 
          path="/admin/project/*" 
          element={
            <RoleBasedRoute allowedRoles={["projectadmin"]}>
              <ProjectAdminLayout />
            </RoleBasedRoute>
          }
        >
          {/* Default */}
          <Route index element={<Navigate to="overview" replace />} />

          {/* Setup */}
        <Route path="profile" element={<ProfileOverview />} />
          {/* <Route path="profile-setup" element={<ProfileSetup />} /> */}

          {/* Agents */}
          <Route path="agents-domains" element={<AddSupport />} />
          <Route path="agents" element={<SupportAgentDirectory />} />
          <Route path="agents/:id" element={<SupportAgentOverview />} />
          <Route path="agents/bulk-upload" element={<BulkUploadSupport />} />
          <Route path="agents/edit-agent/: " element={<EditSupport />} />

          {/* Employees */}
          <Route path="employees" element={<EmployeeDirectory />} />
          <Route path="employees/onboard" element={<AddEmployee />} />
          <Route path="employees/bulk-upload" element={<BulkUploadEmployee />} />
          <Route path="employees/:employeeId" element={<EmployeeOverview />} />

          {/* Queues */}
          <Route path="queues" element={<QueueDirectory />} />
          <Route path="queues/view/:queueId" element={<QueueOverview />} />
          <Route
            path="queues/create-assign-queue"
            element={<AddQueue />}
          />
          <Route
            path="queues/edit-assign-queue/:queueId"
            element={<EditQueue />}
          />
        
          {/* Knowledge Base */}
          <Route path="knowledge-base" element={<KbDirectory />} />
          <Route
            path="knowledge-base/setup"
            element={<KbCategory />}
          />
          <Route
            path="knowledge-base/create-knowledge-base"
            element={<AddKb />}
          />
          <Route
            path="knowledge-base/edit-knowledge-base/:articleId"
            element={<EditKb />}
          />
          <Route
            path="knowledge-base/bulk-upload-knowledge-base"
            element={<BulkUploadKb />}
          />
          <Route
            path="knowledge-base/article/:articleId"
            element={<KbOverview />}
          />

          {/* Support */}
          <Route path="tickets" element={<TicketsDirectory />} />
          <Route path="incidents" element={<TicketsDirectory forcedTab="INCIDENT" />} />
          <Route path="requests" element={<TicketsDirectory forcedTab="REQUEST" />} />
          <Route path="tickets/:ticketId" element={<TicketsOverview />} />
          {/* DEF-114: section-scoped detail URLs keep the Incidents/Requests
              sidebar item highlighted; both render the same detail component. */}
          <Route path="incidents/:ticketId" element={<TicketsOverview />} />
          <Route path="requests/:ticketId" element={<TicketsOverview />} />
          <Route path="app-domain" element={<AppDomain />} />
          <Route path="sla-policies" element={<SlaPolicies />} />

          {/* Analytics */}
          <Route path="reports" element={<Reports />} />
          <Route path="overview" element={<ProjectAdminDashboard />} />
          <Route path="notifications" element={<NotificationsPage title="Notifications" ticketBasePath="/admin/project/tickets" />} />

          {/* Application & Domain */}
          <Route path="applications/:appId" element={<ApplicationOverview />} />
          <Route path="custom-form/:id" element={<CustomForm />} />
          <Route path="add-request/:appId" element={<CustomForm />} />
          <Route path="form-render" element={<FormRenderer />} />
          <Route path="domains/:domainId" element={<DepartmentOverview />} />
          {/* Domain form builder — same CustomForm component, different mode */}
          <Route path="domain-form/:domainId" element={<CustomForm mode="domain" />} />
          <Route path="domain-form/edit/:id" element={<CustomForm mode="domain" />} />

          {/* Testing */}
          <Route path="test" element={<Testing />} />
          
          {/* Catch all 404 for project admin routes */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* PUBLIC REQUEST FORM – no auth, top-level */}
        <Route
          path="/public/request-form/:requestTypeId"
          element={<ApplicationRequestForm />}
        />

        {/* PUBLIC DASHBOARD-STYLE FORM – no auth, used as iframe target */}
        <Route
          path="/public/dashboard-form"
          element={<PublicDashboardForm />}
        />

        {/* SUPPORT AGENTS ROUTES */}
        <Route
          path="/admin/support/*"
          element={
            <RoleBasedRoute allowedRoles={["support"]}>
              <SupportAgentLayout />
            </RoleBasedRoute>
          }
        >
          {/* MY WORK */}
          <Route path="overview" element={<Overview />} />
          <Route path="notifications" element={<NotificationsPage title="Notifications" ticketBasePath="/admin/support/tickets" />} />
          <Route path="inbox" element={<TicketDetail />} />
          <Route path="inbox/:ticketId" element={<TicketDetail />} />
          <Route path="tickets" element={<TicketPage />} />
          <Route path="tickets/:ticketId" element={<TicketDetail />} />
          <Route path="queues" element={<Queues />} />
          <Route path="knowledge-base" element={<KnowledgeBase />} />
          <Route
            path="knowledge-base/new"
            element={
              <RequireCapability capability="KB_AUTHOR" fallback="/admin/support/knowledge-base">
                <NewKBDraft />
              </RequireCapability>
            }
          />
          <Route
            path="knowledge-base/article/:articleId"
            element={<PreviewKBSupportAgents />}
          />
          <Route path="my-profile" element={<SupportProfile />} />
          <Route
            path="forms"
            element={
              <RequireCapability capability="FORM_DESIGNER" fallback="/admin/support/knowledge-base">
                <AgentFormsList />
              </RequireCapability>
            }
          />
          <Route
            path="forms/new"
            element={
              <RequireCapability capability="FORM_DESIGNER" fallback="/admin/support/knowledge-base">
                <AgentFormDesigner />
              </RequireCapability>
            }
          />
          <Route
            path="forms/:formId"
            element={
              <RequireCapability capability="FORM_DESIGNER" fallback="/admin/support/knowledge-base">
                <AgentFormView />
              </RequireCapability>
            }
          />

          {/* Catch all 404 for support agent routes */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* SUPERADMIN ROUTES */}
        <Route
          path="/admin/super/*"
          element={
            <RoleBasedRoute allowedRoles={["superadmin"]}>
              <SuperAdminLayout />
            </RoleBasedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardOverview />} />
          <Route path="notifications" element={<NotificationsPage title="Notifications" />} />
          <Route path="add-domain" element={<AddDomainPage />} />

          {/* Client Management Routes */}
          <Route path="clients">
            <Route path="add-client" element={<AddClient />} />
            <Route path="client-dashboard" element={<ClientDashboardOverview />} />
            <Route
              path="client-management/:clientId"
              element={<ClientsManagement />}
            />
            <Route
              path="client-management/edit/:clientId"
              element={<EditClient />}
            />
            <Route
              path="client-management/add-application/:clientId"
              element={<AddApplication />}
            />
            <Route
              path=":clientId/applications/:applicationId/edit"
              element={<EditApplication />}
            />
            <Route
              path="client-management/add-domain/:clientId"
              element={<AddDomainPage />}
            />
            <Route
              path=":clientId/domain/:domainId/edit"
              element={<EditDomain />}
            />
            <Route
              path="client-management/add-project-admin/:clientId"
              element={<AddProjectAdmin />}
            />
          </Route>

          <Route path="project-admins">
            <Route index element={<Navigate to="view" replace />} />
            <Route
              path="edit/:clientId/:adminId"
              element={<EditProjectAdmin />}
            />
            <Route
              path="new"
              element={<AddProjectAdminWithDropdown />}
            />
            <Route path="view" element={<ProjectAdminsPage />} />
            <Route path=":id" element={<ProjectAdminDetailsPage />} />
          </Route>

          <Route path="support-agents">
            <Route index element={<Navigate to="view" replace />} />
            <Route path="view" element={<SupportAgentsPage />} />
            <Route path=":id" element={<AgentDetailsPage />} />
          </Route>

          <Route path="reports" element={<Report />} />
          <Route path="sla" element={<SLAManagement />} />
          <Route path="support-channels" element={<SupportChannels />} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />

          {/* Catch all 404 for superadmin routes */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* MANAGER ROUTES (SLA approvals only) */}
        <Route
          path="/admin/manager/*"
          element={
            <RoleBasedRoute allowedRoles={["manager"]}>
              <ManagerLayout />
            </RoleBasedRoute>
          }
        >
          <Route index element={<Navigate to="sla-approvals" replace />} />
          <Route path="sla-approvals" element={<ManagerSLAApprovals />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* APPLICATION ROUTES (mirrors employee, with iframe-embedded application form) */}
        <Route
          path="/application/*"
          element={
            <RoleBasedRoute allowedRoles={["employee"]}>
              <ApplicationLayout />
            </RoleBasedRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />

          {/* home */}
          <Route path="home" element={<ApplicationDashboard />} />
          <Route path="notifications" element={<NotificationsPage title="Notifications" ticketBasePath="/application/my-request" />} />
          <Route path="home/request-form" element={<ApplicationRequestFormPage />} />
          <Route path="home/chatbot" element={<ApplicationChatbot />} />

          {/* MY Request */}
          <Route path="my-request" element={<ApplicationMyRequest />} />
          <Route path="my-request/:id" element={<ApplicationRequestOverview />} />

          {/* my profile */}
          <Route path="my-profile" element={<ApplicationProfile />} />

          {/* Catch all 404 for application routes */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* EMPLOYEE ROUTES */}
        <Route
          path="/employee/*"
          element={
            <RoleBasedRoute allowedRoles={["employee"]}>
              <EmployeeLayout />
            </RoleBasedRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />

          {/* home */}
          <Route path="home" element={<EmployeeDashboard />} />
          <Route path="notifications" element={<NotificationsPage title="Notifications" ticketBasePath="/employee/my-request" />} />
          <Route path="home/request-form" element={<RequestFormPage />} />
          <Route path="home/chatbot" element={<Chatbot />} />

          {/* Service Catalog (dynamic forms → app fulfilment queue) */}
          <Route path="service-catalog" element={<ServiceCatalog />} />
          <Route path="service-catalog/:source/:requestTypeId" element={<CatalogFormPage />} />

          {/* MY Request */}
          <Route path="my-request" element={<MyRequest />} />
          <Route path="my-request/:id" element={<RequestOverview />} />

          {/* my profile */}
          <Route path="my-profile" element={<EmployeeProfile />} />
          
          {/* Catch all 404 for employee routes */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>

        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/auth/sign-in" replace />} />
        
        {/* Catch all 404 for undefined routes */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
      </Suspense>
    </>
  );
};

/* ------------------ MAIN APP ------------------ */
const App = () => {
  const [initialData] = useState(() =>
    typeof window !== "undefined" ? window.__INITIAL_DATA__ || null : null
  );

  return (
    <InitialDataContext.Provider value={initialData}>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </InitialDataContext.Provider>
  );
};

export default App;